/**
 * Shared daily-puzzle helpers.
 *
 * Daily content should refresh at the player's local midnight and be stable
 * across reloads for the same local date. The helpers here deliberately avoid
 * UTC conversion so "today" matches how residents experience the day.
 *
 * Also the general-purpose seeding primitives for anything that must reproduce:
 * a saved print document stores its seed and reprints byte-for-byte.
 *
 * Known duplication, stated rather than papered over: `bingo/rng.ts` carries its
 * own FNV-1a + mulberry32 pair, and `word-search/grid.ts` a third mulberry32
 * (`makeRand`). They are algorithmically the same. They are NOT unified here
 * because each already seeds shipped content — collapsing them would silently
 * change which puzzle a given day produces, and in bingo's case which card a
 * resident is holding. Any unification has to be a deliberate, versioned break.
 */
/** Local date key in YYYYMMDD form. */
export function localDateKey(now = new Date()) {
    const yyyy = String(now.getFullYear()).padStart(4, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
}
/** Deterministic 32-bit FNV-1a hash. */
export function hashSeed(input) {
    let h = 0x811c9dc5;
    for (let i = 0; i < input.length; i += 1) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/** Scope + local date -> stable daily seed. */
export function dailySeed(scope, now = new Date()) {
    return hashSeed(`${scope}:${localDateKey(now)}`);
}
/** Small deterministic PRNG for seeded shuffles. */
export function makeSeededRandom(seed) {
    let s = seed >>> 0;
    return function rand() {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
//# sourceMappingURL=seed.js.map